import TextField from "@/components/form/hook-form/textfield";
import { BaseModal } from "@/components/modal/base-modal";
import SegmentedControl from "@/components/toggle/segmented-control";
import { FilledButton } from "@/components/ui/buttons/FilledButton";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  convertWeight,
  normalizeInitialWeight,
  normalizeWeightForSubmit,
  sanitizeWeightInput,
} from "./weight-utils";

interface WeightInputModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  defaultValue: string;
  defaultUnit: string;
  onSelect: (value: string, unit: "kg" | "lbs") => void;
}

type WeightInputFormValues = {
  weight: string;
};

export const WeightInputModal = memo(function WeightInputModal({
  isOpen,
  onDismiss,
  defaultValue,
  defaultUnit,
  onSelect,
}: WeightInputModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [unit, setUnit] = useState<"kg" | "lbs">("kg");
  const form = useForm<WeightInputFormValues>({
    defaultValues: {
      weight: "",
    },
  });
  const watchedWeight = useWatch({
    control: form.control,
    name: "weight",
  });

  useEffect(() => {
    if (!isOpen) return;

    setUnit(defaultUnit === "lbs" ? "lbs" : "kg");
    form.reset({
      weight: normalizeInitialWeight(defaultValue),
    });

    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 250);

    return () => {
      window.clearTimeout(timer);
    };
  }, [defaultUnit, defaultValue, form, isOpen]);

  useEffect(() => {
    const normalizedWeight = sanitizeWeightInput(watchedWeight ?? "");
    if (normalizedWeight === (watchedWeight ?? "")) return;

    form.setValue("weight", normalizedWeight, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [form, watchedWeight]);

  const canConfirm = useMemo(() => {
    const parsed = parseFloat(watchedWeight ?? "");
    return Number.isFinite(parsed) && parsed > 0;
  }, [watchedWeight]);

  return (
    <BaseModal isOpen={isOpen} onDismiss={onDismiss} initialBreakpoint={0.8} breakpoints={[0, 0.8]}>
      <div className="safe-area-bottom flex flex-col gap-3 p-9 pt-9">
        <h3 className={cn(textVariants.title3, "text-center text-black")}>Your Weight</h3>

        <div className="flex flex-col gap-4">
          <SegmentedControl
            options={[
              { value: "kg", label: "kg" },
              { value: "lbs", label: "lbs" },
            ]}
            value={unit}
            onChange={(val) => {
              const nextUnit = val as "kg" | "lbs";
              const convertedWeight = convertWeight(watchedWeight ?? "", unit, nextUnit);
              setUnit(nextUnit);
              form.setValue("weight", convertedWeight, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
              });
            }}
            classNames={{
              container: "h-11 w-full p-1",
              button: "h-full text-black",
              buttonActive:
                "h-full text-black shadow-[-4px_-4px_40px_0px_rgba(215,214,214,0.15),4px_4px_24px_0px_rgba(215,214,214,0.15)]",
            }}
          />

          <TextField
            control={form.control}
            name="weight"
            type="text"
            inputMode="decimal"
            placeholder="Add weight (e.g. 50.3)"
            inputRef={inputRef}
            className="h-[46px] rounded-[14px] border-[var(--color-border-input-default)] px-3 shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]"
          />

          <FilledButton
            color="purple"
            text="Confirm Weight"
            disabled={!canConfirm}
            onClick={() => {
              const normalizedWeight = normalizeWeightForSubmit(watchedWeight ?? "");
              if (!normalizedWeight) return;
              onSelect(normalizedWeight, unit);
              onDismiss();
            }}
          />
        </div>
      </div>
    </BaseModal>
  );
});

export default WeightInputModal;
